/**
 * PlayerHeroCtaClusterContainer — desktop player-page hero CTA cluster
 * (SE-5022). Replaces the legacy grey "+" icon with a two-CTA cluster:
 *
 *   1. Secondary outline "+ Create" — Report / Note popover.
 *   2. Primary solid "+ Add to Watchlist" — dropdown of editable
 *      watchlists with checkbox-toggle, plus inline "+ New List" that
 *      opens the canonical CreateWatchlistModal.
 *
 * Behaviour rules come from Notion 8.2 / 8.3 (source-precedence —
 * Notion owns behaviour):
 *   - Lists of every role (OWNER / EDITOR / VIEWER) are shown. The earlier
 *     OWNER/EDITOR-only filter hid VIEWER-shared lists, so coworkers a list
 *     was shared with couldn't reach it here.
 *   - Only OWNER / EDITOR rows carry a membership checkbox (SE-8306). The
 *     backend gates both add and remove on role, so a VIEWER row's toggle
 *     was an affordance for a write that always failed; VIEWER rows stay
 *     listed but render read-only, with the reason on the row.
 *   - Toggle a checkbox fires the add / remove mutation immediately
 *     with optimistic UI; revert + toast on error.
 *   - "+ New List" → CreateWatchlistModal → on success the new list
 *     is auto-added for this player and pre-checked in the dropdown.
 *     If the auto-add fails, surface an error toast and leave the
 *     (empty) list in place — never auto-delete.
 *   - Create dropdown closes on selection. Report navigates to the
 *     player's matches selection ("./{playerId}/matches" via
 *     useExpandedPlayerUrl). Note flips the visible tab to Reports &
 *     Notes and signals the auto-create reactive variable.
 *
 * Out of scope: "Saved in watchlists" indicator (SE-5024), the
 * rendering-context modal/scrim plumbing (SE-5025), and the mobile
 * FAB (SE-5083). This component only renders on desktop — the caller
 * still mounts MobileActionBar on small screens.
 * @remarks Container component — owns mutations, query, and modal state.
 */

import { type Href, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";

import CreateDropdownView from "./CreateDropdown";
import PlayerHeroCtaClusterView from "./PlayerHeroCtaClusterView";
import WatchlistDropdownView, {
  type WatchlistDropdownItem,
} from "./WatchlistDropdown";

import DesktopPickerOverlay from "@/features/player-detail/components/molecules/DesktopPickerOverlay";

import AddToPipelineDialog from "@/features/player-kanban/components/organisms/AddToPipelineDialog";

import CreateWatchlistModal from "@/components/molecules/CreateWatchlistModal";
import { Button } from "@/components/atoms/Button";
import { useTheme } from "@/providers/ThemeProvider";
import {
  type PlayerSearchWatchlistMetaFragment,
  usePlayerSearchAddPlayerToWatchlistMutation,
  usePlayerSearchRemovePlayerFromWatchlistMutation,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import {
  canEditWatchlistMembership,
  MY_WATCHLISTS_PAGE_SIZE,
} from "@/features/watchlist/constants";
import useExpandedPlayerUrl from "@/hooks/useExpandedPlayerUrl";
import { useToastNotification } from "@/hooks/useToastNotification";
import { modifyWatchlistEntityPlayers } from "@/utils/handleWatchlistPlayers";
import {
  playerAutoCreateNoteVar,
  playerPreviewOpenVar,
  selectPlayerTab,
} from "@/stores/player";

import { MoreHorizontal, Plus } from "lucide-react-native";

/** Operation name passed to refetchQueries so siblings stay in sync */
const MY_USER_WATCHLISTS_OP = "WatchlistListMyUserWatchlists";

/** Public props */
export interface PlayerHeroCtaClusterContainerProps {
  /** Player id — required to fire add / remove mutations and the matches navigation */
  readonly playerId: string;
  /**
   * "Edit player" — manual players only (undefined hides the row). Surfaced in
   * the meatball (⋯) menu per the V1.6 design (SE-5179).
   */
  readonly onEditPlayer?: () => void;
  /** "Delete player" — manual players only (undefined hides the row). */
  readonly onDeletePlayer?: () => void;
  /** "Transfer reports & notes" — admin / flagged on manual players (undefined hides the row). */
  readonly onTransferReports?: () => void;
  /** Whether the transfer action is disabled (0 reports and 0 notes). */
  readonly isTransferDisabled?: boolean;
}

/** Visible checked state = optimistic override ?? server membership */
type OptimisticToggles = Readonly<Record<string, boolean>>;

/**
 * Renders the desktop CTA cluster.
 * @param props - Container props
 * @param props.playerId - The player id for mutation variables and matches navigation
 * @param props.onEditPlayer - "Edit player" handler — manual players only (undefined hides the row)
 * @param props.onDeletePlayer - "Delete player" handler — manual players only (undefined hides the row)
 * @param props.onTransferReports - "Transfer reports & notes" handler — admin / flagged only (undefined hides the row)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 */
const PlayerHeroCtaClusterContainer = ({
  playerId,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
}: PlayerHeroCtaClusterContainerProps) => {
  const toast = useToastNotification();
  const router = useRouter();
  const matchesUrl = useExpandedPlayerUrl(`/players/${playerId}/matches`);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  /** Whether the player has any management actions (gates the meatball menu) */
  const hasManagementActions = Boolean(
    onEditPlayer || onDeletePlayer || onTransferReports
  );

  const [isWatchlistOpen, setIsWatchlistOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // SE-5499: the reusable pick-pipeline-and-stage dialog (profile add path).
  const [isPipelineDialogOpen, setIsPipelineDialogOpen] = useState(false);
  const [optimisticToggles, setOptimisticToggles] = useState<OptimisticToggles>(
    {}
  );
  /**
   * Watchlist ids with an in-flight add/remove mutation. A rapid second tap
   * on the same row would otherwise start a parallel mutation and let the
   * earlier request's `setToggle(undefined)` overwrite the newer optimistic
   * state — committing the wrong final membership if the server processes
   * them out of order. We early-return for any id already in this set.
   */
  const [pendingToggleIds, setPendingToggleIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  const { data, loading, refetch } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
  });

  const [addPlayerToWatchlist] = usePlayerSearchAddPlayerToWatchlistMutation();
  const [removePlayerFromWatchlist] =
    usePlayerSearchRemovePlayerFromWatchlistMutation();

  /**
   * Every watchlist the user is a member of, regardless of role. VIEWER lists
   * are intentionally included so lists shared with a coworker are reachable
   * here.
   *
   * SE-8306: the backend DOES gate player add/remove on role — both mutations
   * pass through `lockWatchlistAggregate`, which resolves the list only for an
   * OWNER or EDITOR and answers `NotFoundException` otherwise. (This comment
   * previously asserted the opposite; it was true when written and was
   * falsified by SE-8299's role gate.) So membership is read-only on a VIEWER
   * row, and `canEditWatchlistMembership` below is what decides whether the
   * row gets a checkbox.
   */
  const watchlistEdges = useMemo(
    () => data?.listMyUserWatchlists?.edges ?? [],
    [data]
  );

  /** Ids of watchlists that already contain this player (server truth) */
  const serverCheckedIds = useMemo(
    (): readonly string[] =>
      watchlistEdges
        .filter(edge =>
          edge.node.watchlist.players?.edges?.some(
            pe => pe.node.id === playerId
          )
        )
        .map(edge => edge.node.watchlist.id),
    [watchlistEdges, playerId]
  );

  /** Items for the dropdown — visible checked = optimistic ?? server */
  const items = useMemo(
    (): readonly WatchlistDropdownItem[] =>
      watchlistEdges.map(edge => {
        const id = edge.node.watchlist.id;
        const serverChecked = serverCheckedIds.includes(id);
        const isChecked = optimisticToggles[id] ?? serverChecked;
        return {
          id,
          name: edge.node.watchlist.name,
          isChecked,
          canEdit: canEditWatchlistMembership(edge.node.role),
        };
      }),
    [watchlistEdges, serverCheckedIds, optimisticToggles]
  );

  /** Whether the player is in any list — drives the primary CTA label */
  const isInAnyList = items.some(item => item.isChecked);
  const primaryLabel = isInAnyList ? "Update list" : "Add to Watchlist";

  /**
   * Apply or clear an optimistic toggle for a single id.
   * Setting `desired === undefined` removes the override.
   */
  const setToggle = useCallback(
    (id: string, desired: boolean | undefined) =>
      setOptimisticToggles(prev => {
        if (desired === undefined) {
          if (!(id in prev)) return prev;
          return Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== id)
          );
        }
        return { ...prev, [id]: desired };
      }),
    []
  );

  /** Toggle a single list — optimistic check then fire add / remove */
  const handleToggle = useCallback(
    async (watchlistId: string) => {
      // Ignore a tap while this row is already mid-mutation — prevents the
      // earlier request from racing and overwriting newer optimistic state.
      if (pendingToggleIds.has(watchlistId)) return;
      const wasChecked = items.find(i => i.id === watchlistId)?.isChecked;
      if (wasChecked === undefined) return;
      const targetChecked = !wasChecked;
      setToggle(watchlistId, targetChecked);
      setPendingToggleIds(prev => new Set([...prev, watchlistId]));

      // SE-6777 - `MY_USER_WATCHLISTS_OP` selects no `playerCount`, so the
      // refetch could never move the count badge. Each branch carries its own
      // `update` because the two generated mutations have distinct payload
      // types; one callback over the union would not narrow.
      const runMutation = targetChecked
        ? () =>
            addPlayerToWatchlist({
              variables: { input: { watchlistId, playerId } },
              refetchQueries: [MY_USER_WATCHLISTS_OP],
              awaitRefetchQueries: true,
              update: (cache, { data, errors }) => {
                if (!data?.addPlayerToWatchlist || errors) return;
                modifyWatchlistEntityPlayers(
                  cache,
                  watchlistId,
                  playerId,
                  "add"
                );
              },
            })
        : () =>
            removePlayerFromWatchlist({
              variables: { input: { watchlistId, playerId } },
              refetchQueries: [MY_USER_WATCHLISTS_OP],
              awaitRefetchQueries: true,
              update: (cache, { data, errors }) => {
                if (!data?.removePlayerFromWatchlist || errors) return;
                modifyWatchlistEntityPlayers(
                  cache,
                  watchlistId,
                  playerId,
                  "remove"
                );
              },
            });

      try {
        await runMutation();
        // Clear the optimistic override now that the refetch has reconciled
        // server state — leaving it in place would mask future server-driven
        // changes for this row (e.g. another tab removing the player).
        setToggle(watchlistId, undefined);
      } catch (error) {
        setToggle(watchlistId, undefined);
        toast.show({
          action: "danger",
          description: targetChecked
            ? "Could not add to list. Please try again."
            : "Could not remove from list. Please try again.",
        });
        // Surface the underlying error for developers
        // eslint-disable-next-line no-console -- intentional for error visibility
        console.warn("Watchlist toggle failed:", error);
      } finally {
        setPendingToggleIds(
          prev => new Set([...prev].filter(id => id !== watchlistId))
        );
      }
    },
    [
      items,
      pendingToggleIds,
      setToggle,
      addPlayerToWatchlist,
      removePlayerFromWatchlist,
      playerId,
      toast,
    ]
  );

  /**
   * Pre-built per-row handlers — keeps the View free of inline functions.
   * Returns the Promise from handleToggle so tests can await the full
   * mutation lifecycle inside an `act`; React Native's onPress signature
   * accepts `() => unknown`, so the void-discard is fine in production.
   *
   * SE-8306: read-only (VIEWER) rows get no entry. This is the single gate on
   * the write — the View reads the same `canEdit` to decide what to render, so
   * a row without a handler is also a row without a checkbox, and there is no
   * second, unreachable guard inside `handleToggle` pretending to be tested.
   */
  const toggleHandlers = useMemo(
    () =>
      items.reduce<Record<string, () => Promise<void>>>(
        (acc, item) =>
          item.canEdit
            ? {
                ...acc,
                [item.id]: () => handleToggle(item.id),
              }
            : acc,
        {}
      ),
    [items, handleToggle]
  );

  const handleOpenWatchlist = useCallback(() => setIsWatchlistOpen(true), []);
  const handleCloseWatchlist = useCallback(() => setIsWatchlistOpen(false), []);
  const handleOpenCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);
  const handleOpenMenu = useCallback(() => setIsMenuOpen(true), []);
  const handleCloseMenu = useCallback(() => setIsMenuOpen(false), []);
  const handleOpenPipelineDialog = useCallback(
    () => setIsPipelineDialogOpen(true),
    []
  );
  const handleClosePipelineDialog = useCallback(
    () => setIsPipelineDialogOpen(false),
    []
  );

  /** Open the canonical create-watchlist modal — close the dropdown first */
  const handleOpenCreateModal = useCallback(() => {
    setIsWatchlistOpen(false);
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  /**
   * After the new list is created on the server, auto-add the player.
   * If the add fails, leave the (empty) list in place per AC — do not
   * destructively delete it. The success toast for the list itself is
   * shown by CreateWatchlistModal.
   */
  const handleListCreated = useCallback(
    async (created: PlayerSearchWatchlistMetaFragment) => {
      // Pre-check the new list while the mutation is in flight
      setToggle(created.id, true);
      try {
        await addPlayerToWatchlist({
          variables: { input: { watchlistId: created.id, playerId } },
          refetchQueries: [MY_USER_WATCHLISTS_OP],
          awaitRefetchQueries: true,
          // SE-6777 - without this the freshly created list's badge reads 0
          // even though the auto-add succeeded.
          update: (cache, { data, errors }) => {
            if (!data?.addPlayerToWatchlist || errors) return;
            modifyWatchlistEntityPlayers(cache, created.id, playerId, "add");
          },
        });
        setToggle(created.id, undefined);
      } catch (error) {
        setToggle(created.id, undefined);
        await refetch();
        toast.show({
          action: "danger",
          description: `Could not add player to "${created.name}". The list was created — please retry adding the player.`,
        });
        // eslint-disable-next-line no-console -- intentional for error visibility
        console.warn("Auto-add to new list failed:", error);
      }
    },
    [addPlayerToWatchlist, playerId, refetch, setToggle, toast]
  );

  /**
   * Report selection — navigate to matches selection, close popover. When the
   * player hero is shown inside the preview modal (which is dismissed on
   * navigation), flag the Select Matches screen to drop its back arrow so it
   * doesn't dead-end (SE-5185 follow-up).
   */
  const handleSelectReport = useCallback(() => {
    setIsCreateOpen(false);
    const url = playerPreviewOpenVar()
      ? `${matchesUrl}?hideBackButton=true`
      : matchesUrl;
    router.push(url as Href);
  }, [router, matchesUrl]);

  /** Note selection — switch the player's visible tab + signal auto-create */
  const handleSelectNote = useCallback(() => {
    setIsCreateOpen(false);
    // SE-8285: this handler does not navigate — it switches the tab for the
    // player already on screen, so it names that one.
    selectPlayerTab(playerId, "reportsNotes");
    playerAutoCreateNoteVar(true);
  }, [playerId]);

  /**
   * Manual-player management selections — close the meatball menu first, then
   * invoke the upstream handler (which owns the edit modal / delete dialog /
   * transfer modal). Each is undefined for non-manual players, so the row is
   * hidden and the meatball itself is not rendered.
   */
  const handleSelectEdit = useCallback(() => {
    setIsMenuOpen(false);
    onEditPlayer?.();
  }, [onEditPlayer]);

  const handleSelectDelete = useCallback(() => {
    setIsMenuOpen(false);
    onDeletePlayer?.();
  }, [onDeletePlayer]);

  const handleSelectTransfer = useCallback(() => {
    setIsMenuOpen(false);
    onTransferReports?.();
  }, [onTransferReports]);

  /** Stable trigger renderers — required to keep the View free of new fns */
  const renderCreateTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Button
        {...triggerProps}
        label="Create"
        variant="outline"
        tone="secondary"
        size="md"
        icon={Plus}
        iconTone="accent"
        labelTone="accent"
        labelVariant="title-sm"
        className="h-10 rounded-sm px-4"
        accessibilityLabel="Create"
      />
    ),
    []
  );

  const renderWatchlistTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Button
        {...triggerProps}
        label={primaryLabel}
        variant="solid"
        tone="primary"
        size="md"
        icon={Plus}
        labelVariant="title-sm"
        className="h-10 rounded-sm px-4"
        accessibilityLabel={primaryLabel}
      />
    ),
    [primaryLabel]
  );

  /** Meatball (⋯) trigger — manual-player management menu (V1.6 design, SE-5179) */
  const renderMenuTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Button
        {...triggerProps}
        variant="outline"
        tone="secondary"
        size="md"
        icon={MoreHorizontal}
        iconTone="accent"
        className="size-10 rounded-sm p-0"
        accessibilityLabel="More actions"
      />
    ),
    []
  );

  /** "Add to Pipeline" trigger — opens the reusable dialog (SE-5499). */
  const addToPipelineTrigger = (
    <Button
      label="Add to Pipeline"
      variant="outline"
      tone="secondary"
      size="md"
      icon={Plus}
      iconTone="accent"
      labelTone="accent"
      labelVariant="title-sm"
      className="h-10 rounded-sm px-4"
      accessibilityLabel="Add to Pipeline"
      testID="player-hero-add-to-pipeline"
      onPress={handleOpenPipelineDialog}
    />
  );

  return (
    <>
      <PlayerHeroCtaClusterView
        primaryLabel={primaryLabel}
        addToPipelineTrigger={addToPipelineTrigger}
        isWatchlistOpen={isWatchlistOpen}
        onOpenWatchlist={handleOpenWatchlist}
        onCloseWatchlist={handleCloseWatchlist}
        isCreateOpen={isCreateOpen}
        onOpenCreate={handleOpenCreate}
        onCloseCreate={handleCloseCreate}
        renderCreateTrigger={renderCreateTrigger}
        renderWatchlistTrigger={renderWatchlistTrigger}
        watchlistDropdown={
          <WatchlistDropdownView
            items={items}
            toggleHandlers={toggleHandlers}
            isLoading={loading && items.length === 0}
            onCreateList={handleOpenCreateModal}
          />
        }
        createDropdown={
          <CreateDropdownView
            onSelectReport={handleSelectReport}
            onSelectNote={handleSelectNote}
          />
        }
        isMenuOpen={isMenuOpen}
        onOpenMenu={handleOpenMenu}
        onCloseMenu={handleCloseMenu}
        renderMenuTrigger={hasManagementActions ? renderMenuTrigger : undefined}
        managementMenu={
          hasManagementActions ? (
            <DesktopPickerOverlay
              isDark={isDark}
              onEditPlayer={onEditPlayer ? handleSelectEdit : undefined}
              onDeletePlayer={onDeletePlayer ? handleSelectDelete : undefined}
              onTransferReports={
                onTransferReports ? handleSelectTransfer : undefined
              }
              isTransferDisabled={isTransferDisabled}
            />
          ) : undefined
        }
      />
      <CreateWatchlistModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onCreated={handleListCreated}
      />
      {/* key flips on open so the dialog remounts with a fresh board/stage
          selection each time — a cancelled pick must never survive into the
          next open (key-reset, the repo's sanctioned alternative to a
          reset-effect). */}
      <AddToPipelineDialog
        key={isPipelineDialogOpen ? "atp-open" : "atp-closed"}
        isOpen={isPipelineDialogOpen}
        onClose={handleClosePipelineDialog}
        playerId={playerId}
      />
    </>
  );
};

PlayerHeroCtaClusterContainer.displayName = "PlayerHeroCtaClusterContainer";

export default PlayerHeroCtaClusterContainer;
